/**
 * CockpitResponse
 * @author ftgibran
 */
import {$, Model} from 'simpli-web-sdk'
import moment from 'moment'
import * as Helper from '@/helpers'
import CockpitRequest from '@/model/request/CockpitRequest'
import {Period} from '@/enums/Period'
import {PerformanceState} from '@/enums/PerformanceState'

export default class CockpitResponse extends Model {
  readonly $name: string = 'CockpitResponse'

  initialized: number | null = null
  shipping: number | null = null
  waiting: number | null = null

  cargaSuccessfullyDelivered: number | null = null
  cargaWithOccurrence: number | null = null
  cargaWithDevolution: number | null = null

  graphPeriod: Period | null = Period.HOURLY
  graphDataInicio: string | null = null
  graphRange = 0

  cacheDate: string | null = null

  performanceState = PerformanceState.HIDDEN

  graphCargaSuccessfullyDelivered: number[] = []
  graphCargaWithOccurrence: number[] = []
  graphCargaWithDevolution: number[] = []

  performanceInPercent = 0

  get total() {
    return (this.cargaSuccessfullyDelivered || 0) + (this.cargaWithOccurrence || 0) + (this.cargaWithDevolution || 0)
  }

  async getCockpit(request: CockpitRequest, spinner = 'getCockpit') {
    const fetch = async () => await this.POST(`/user/me/cockpit`, request)
    return await $.await.run(fetch, spinner)
  }

  async getCockpitPerformance(request: CockpitRequest, spinner = 'getCockpitPerformance') {
    const fetch = async () => await this.POST(`/user/me/cockpit/performance`, request)
    return await $.await.run(fetch, spinner)
  }

  async getCockpitGrafico(request: CockpitRequest, spinner = 'getCockpitGrafico') {
    const fetch = async () => await this.POST(`/user/me/cockpit/grafico`,
      request,
      { headers: { 'X-Ignore-Errors': '1' } },
      )
    return await $.await.run(fetch, spinner)
  }

  get graphTitle() {
    const date = this.graphDataInicio
    if (!date) return ''
    if (this.graphPeriod === Period.HOURLY) {
      if (this.performanceState === PerformanceState.ORIGEM) {
        return $.t('classes.CockpitResponse.columns.deliveryDelayedDay', {
          date: moment(date).utcOffset('-03:00', false).format($.t('dateFormat.date')),
        })
      }
      if (this.performanceState === PerformanceState.EMBARCADOR) {
        return $.t('classes.CockpitResponse.columns.deliveryDelayedTransporterDay', {
          date: moment(date).utcOffset('-03:00', false).format($.t('dateFormat.date')),
        })
      }
      return $.t('classes.CockpitResponse.columns.deliveryDay', {
        date: moment(date).utcOffset('-03:00', false).format($.t('dateFormat.date')),
      })
    } else if (this.graphPeriod === Period.DAILY) {
      return $.t('classes.CockpitResponse.columns.deliveryMonth', {
        month: moment(date).utcOffset('-03:00', false).format('MMMM'),
        year: moment(date).utcOffset('-03:00', false).format('YYYY'),
      })
    } else if (this.graphPeriod === Period.MONTHLY) {
      return $.t('classes.CockpitResponse.columns.deliveryYear', {
        year: moment(date).utcOffset('-03:00', false).format('YYYY'),
      })
    } else if (this.graphPeriod === Period.WEEKLY) {
      return $.t('classes.CockpitResponse.columns.deliveryWeek', {
        year: moment(date).utcOffset('-03:00', false).format('YYYY'),
      })
    }

    return ''
  }
  get graphDataUpdater() {
    const date = this.cacheDate

    if (date) {
    return $.t('classes.CockpitResponse.columns.checkUpdateDate', {
      date: moment(date).format($.t('DD/MM/YYYY, (H:mm)')),
    })
    }

  }

  get graphColors() {
    return [
      '#547b73',
      '#ffc61a',
      '#eb5757',
    ]
  }

  get graphLegends() {
    return [
      $.t('classes.CockpitResponse.columns.cargaSuccessfullyDelivered'),
      $.t('classes.CockpitResponse.columns.cargaWithOccurrence'),
      $.t('classes.CockpitResponse.columns.cargaWithDevolution'),
    ]
  }

  get graphValues() {
    const values1 = this.graphCargaSuccessfullyDelivered || []
    const values2 = this.graphCargaWithOccurrence || []
    const values3 = this.graphCargaWithDevolution || []

    return [values1, values2, values3]
  }

  get graphLabels() {
    const list = [...Array(this.graphRange)]
    if (this.graphRange === 24) {
      return list.map((val, i) => `${$.filter.pad(`${i || 0}`, 2)}h`)
    } else if (this.graphRange === 12) {
      return list.map((val, i) => moment($.filter.pad(`${(i + 1) || 0}`, 2), 'MM').format('MMM'))
    } else {
      return list.map((val, i) => $.filter.pad(`${(i + 1) || 0}`, 2))
    }
  }
}
